'use client';

import { useCallback } from 'react';
import type { InternalNetworkDto } from '@terra/shared-contracts-internal-network';
import {
  InternalNetworkScopeContent,
  isInternalNetworkScopeLocked,
  toasterApi,
  useInternalNetworkScopeDraft,
  type InternalNetworkScopePatch,
} from '@terra/ui-design-system';
import { useTranslations } from 'next-intl';
import { useUpdateInternalNetworkScope } from '../../../../hooks/api/useInternalNetworkScopeSettings';
import { InternalNetworkAttackPathScopePanel } from './InternalNetworkAttackPathScopePanel';

type InternalNetworkScopeSettingsPanelProps = {
  surface: InternalNetworkDto;
  networkId: string;
  isReadOnly: boolean;
  isDiscoveryActive: boolean;
};

export function InternalNetworkScopeSettingsPanel({
  surface,
  networkId,
  isReadOnly,
  isDiscoveryActive,
}: InternalNetworkScopeSettingsPanelProps) {
  const tSettings = useTranslations('internalNetwork.settings');
  const updateScope = useUpdateInternalNetworkScope(networkId);
  const { mutateAsync: updateInternalNetworkScope } = updateScope;
  const handleAutoSave = useCallback(
    async (patch: InternalNetworkScopePatch) => {
      try {
        await updateInternalNetworkScope(patch);
        toasterApi.success({ title: tSettings('saveSuccess') });
      } catch (error: unknown) {
        console.error(
          new Error('Failed to auto-save internal network scope', {
            cause: error,
          }),
        );
        toasterApi.error({
          title: tSettings('saveError'),
          description: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [tSettings, updateInternalNetworkScope],
  );
  const draft = useInternalNetworkScopeDraft({
    stored: surface,
    isLocked: isInternalNetworkScopeLocked({ isReadOnly, isDiscoveryActive }),
    isSaving: updateScope.isPending,
    onAutoSave: handleAutoSave,
  });

  return (
    <InternalNetworkScopeContent
      {...draft}
      isReadOnly={isReadOnly}
      isDiscoveryActive={isDiscoveryActive}
      isSaving={updateScope.isPending}
      attackPathContent={
        <InternalNetworkAttackPathScopePanel
          networkId={networkId}
          isReadOnly={isReadOnly}
          isDiscoveryActive={isDiscoveryActive}
        />
      }
    />
  );
}
